import {Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {Subscription, timer} from "rxjs";
import {finalize} from 'rxjs/operators';

import {Saseung3} from '../../shared/models/saseung3.model';
import {DEFAULT_SCROLL_SPEED, LIST_TYPE, SearchParam, UI_RELOAD_HEIGHT} from 'src/app/shared/defines/client.const';
import {ListLoader} from "../../shared/interfaces/list-loader";
import {ScrollDownService} from "../../shared/services/scroll-down/scroll-down.service";
import {Saseung3HanjaService} from "../../shared/services/saseung3-hanja/saseung3-hanja.service";
import {Saseung3BookmarkService} from '../../shared/services/saseung3/saseung3-bookmark.service';


@Component({
  selector: 'app-saseung3-detail',
  templateUrl: './saseung3-detail.component.html',
  styleUrls: ['./saseung3-detail.component.scss']
})
export class Saseung3DetailComponent implements OnInit, OnDestroy {
  @ViewChild('container') container: ElementRef;
  @ViewChild('reload') reload: ElementRef;
  @Input() hangul: string | null = null;
  @Input() keyword: string | null = null;
  @Input() hanja: string | null = null;
  @Input() listType: LIST_TYPE = LIST_TYPE.SERVER;
  @Output() closeDetail: EventEmitter<any>;
  @Output() openHelp: EventEmitter<any>;

  // ListLoader 설정
  ll: ListLoader<Saseung3> | null = null;
  private getNextList$: Subscription | null = null;
  private needReload = false;

  // 푸터의 아래로 내리기 버튼 구독
  private scrollDown$: Subscription | null = null;

  get isBookmarkMode(): boolean {
    return this.listType === LIST_TYPE.BOOKMARK;
  }

  // 사성 상세화면 상단에 표시할 내용
  get dispTxt(): string {
    if (this.isBookmarkMode) {
      return '';
    }
    return this.hanja ?? this.keyword ?? this.hangul ?? '';
  }

  // hangul 표시 여부: dispTxt가 1글자 한글인 경우만 숨김
  get showHangul(): boolean {
    if (this.isBookmarkMode) {
      return true;
    }
    const txt = this.dispTxt;
    if (txt.length === 1 && /[가-힣]/.test(txt)) {
      return false;
    }
    return true;
  }


  constructor(
    private saseung3HanjaSvc: Saseung3HanjaService,
    private saseung3BookmarkSvc: Saseung3BookmarkService,
    private scrollDownSvc: ScrollDownService,
  ) {
    this.closeDetail = new EventEmitter<any>();
    this.openHelp = new EventEmitter<any>();
  }

  ngOnInit(): void {
    this.ll = this.listType === LIST_TYPE.BOOKMARK ?
      this.saseung3BookmarkSvc :
      this.saseung3HanjaSvc;

    const param: SearchParam = {};
    if (this.hanja) {
      param.hanja = this.hanja;
    } else if (this.keyword) {
      param.keyword = this.keyword;
    } else if (this.hangul) {
      param.hangul = this.hangul;
    }
    this.ReloadList(param);

    this.scrollDown$ = this.scrollDownSvc.pushScrollDown$().subscribe(isFirst => {
      const scrollDelta = DEFAULT_SCROLL_SPEED * (isFirst ? 2 : 1);  // 스크롤 시작할 때 첫번째는 두배로 스크롤 함.

      this.container?.nativeElement.scrollTo({
        top: this.container.nativeElement.scrollTop + scrollDelta,
        behavior: 'auto',
      });
    });
  }

  ngOnDestroy() {
    this.scrollDownSvc.popScrollDown$();
    if (this.scrollDown$) {
      this.scrollDown$.unsubscribe();
      this.scrollDown$ = null;
    }
  }

  private ReloadList(param: SearchParam): void {
    this.ll?.ResetList(param);
    this.loadList();
  }

  private loadList(): void {
    if (this.getNextList$) {
      console.warn('Saseung3DetailComponent::loadList() already call listList()');
      return;
    }

    if (this.ll.IsComplete()) {
      console.warn('Saseung3DetailComponent::loadList() already load all SaseungHanja');
      return;
    }

    if (this.ll) {
      this.getNextList$ = this.ll.GetNextList().pipe(
        finalize(() => {
          this.getNextList$ = null;

          if (this.needReload) {
            timer(0).subscribe(() => {
              this.needReload = false;
              this.loadList();
            });
          }
        }),
      ).subscribe();
    }
  }

  /****************************************************************************
   * 일정 범위 이상 스크롤되면 남은 사성 한자를 자동 로딩한다.
   ****************************************************************************/

  onScroll($event): void {
    if (!this.ll.IsLoading() && !this.ll.IsComplete()) {
      const tar = $event.target || $event.srcElement || $event.currentTarget;
      if (tar !== this.container.nativeElement) {
        console.error('Saseung3DetailComponent::onScroll() - wrong structure!!');
      }

      const elReload = this.reload.nativeElement;
      if (elReload.offsetTop < window.innerHeight + tar.scrollTop + UI_RELOAD_HEIGHT) {
        if (this.getNextList$ === null) {
          this.loadList();
        } else {
          // 스크롤 중에 이미 로딩이 진행되고 있으면, 로딩이 끝난 뒤에 다시 로딩이 되도록 요청. (비동기처리 순서 꼬임 방지)
          this.needReload = true;
        }
      }
    }
  }


  /****************************************************************************
   * 도움말 팝업창 처리 코드
   ***************************************************************************/
  public OpenSaseungHelp(): void {
    this.openHelp.emit();
  }

  public onClose(): void {
    console.log('Saseung3DetailComponent::onClose()');
    this.closeDetail.emit();
  }

}
